<div class="flex flex-col p-6 gap-5">
  <ep-breadcrumbs>
    <ep-breadcrumb [routerLink]="['/']"><span>Realm</span></ep-breadcrumb>
    <ep-breadcrumb [routerLink]="['/clients']"><span>Clients</span></ep-breadcrumb>
    <ep-breadcrumb><span>Create Client</span></ep-breadcrumb>
  </ep-breadcrumbs>

  <div class="flex items-center justify-between">
    <h1>Create Client & Assign Permissions</h1>
  </div>

  <ep-stepper [currentStep]="currentStepIndex()" orientation="horizontal">
    <ep-step label="Client Setup"> </ep-step>

    <ep-step label="User Assignment"> </ep-step>

    <ep-step label="Summary"> </ep-step>
  </ep-stepper>

  <section class="flex flex-col gap-6">
    <!-- Step 1: Client Setup -->
    @if (currentStep() === 'client') {
      <div class="bg-surface-container rounded-lg p-6">
        <h2 class="heading-md mb-4">Step 1: Choose Client Option</h2>
        <p class="text-surface-on-variant mb-6">
          Create a new client or select an existing one to assign developer and user admin
          permissions.
        </p>

        <ep-tab-container [activeTab]="clientMode()" (tab-change)="setClientMode($event)">
          <ep-tab value="create"><span>Create New Client</span></ep-tab>
          <ep-tab value="select"><span>Select Existing Client</span></ep-tab>
        </ep-tab-container>

        <!-- Create New Client Form -->
        @if (clientMode() === 'create') {
          <div class="p-4">
            <h3 class="heading-sm mb-4">Create New Client</h3>
            <form [formGroup]="createClientForm" class="flex flex-col gap-4">
              <ep-textfield
                label="Client ID"
                formControlName="clientId"
                placeholder="Enter client ID (letters, numbers, hyphens, underscores only)"
                [required]="true"
                ngDefaultControl
              >
                @if (getFieldError('clientId')) {
                  <div slot="error" id="clientId-error">{{ getFieldError('clientId') }}</div>
                }
              </ep-textfield>

              <ep-textfield
                label="Client Name"
                formControlName="name"
                placeholder="Enter client name (letters, numbers, hyphens, underscores only)"
                [required]="true"
                ngDefaultControl
              >
                @if (getFieldError('name')) {
                  <div slot="error" id="name-error">{{ getFieldError('name') }}</div>
                }
              </ep-textfield>

              <ep-textfield
                label="Description"
                formControlName="description"
                placeholder="Enter client description (optional, max 255 characters)"
                variant="textarea"
                rows="3"
                ngDefaultControl
              >
                @if (getFieldError('description')) {
                  <div slot="error" id="description-error">{{ getFieldError('description') }}</div>
                }
              </ep-textfield>

              <div class="flex flex-col gap-2">
                <label class="body-sm font-medium">Client Protocol *</label>
                <div class="flex gap-4" role="radiogroup" aria-labelledby="protocol-label">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      formControlName="protocol"
                      value="openid-connect"
                      class="text-primary focus:ring-primary"
                      aria-describedby="protocol-help"
                    />
                    <span>OpenID Connect</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      formControlName="protocol"
                      value="saml"
                      class="text-primary focus:ring-primary"
                      aria-describedby="protocol-help"
                    />
                    <span>SAML</span>
                  </label>
                </div>
                <p id="protocol-help" class="body-xs text-surface-on-variant">
                  Choose the authentication protocol for this client
                </p>
              </div>
            </form>
          </div>
        }

        <!-- Select Existing Client -->
        @if (clientMode() === 'select') {
          <div class="p-4 flex flex-col gap-2">
            <h3 class="heading-sm mb-4">Select Existing Client</h3>

            <form [formGroup]="searchForm" class="mb-4">
              <ep-combobox
                dynamic="true"
                manualDynamic="true"
                searchDelay="300"
                placeholder="Search by client name, ID, or description (min 2 characters)..."
                size="medium"
                ngDefaultControl
                formControlName="selectedClientValue"
                (search)="searchClients($event)"
                (change)="onClientSelection($event)"
              >
                <label slot="label">Search Clients</label>

                <!-- Dynamic client options -->
                @for (client of searchResults(); track client.id) {
                  <ep-option [value]="client.id" [label]="client.name">
                    <label slot="text">{{ client.name }}</label>
                    <span slot="subtext" class="clamp-text clamp-text--one-line">{{
                      client.description || '&nbsp;'
                    }}</span>

                    <span slot="extra">{{ client.clientId }}</span>
                  </ep-option>
                }

                <!-- No results state -->
                @if (
                  !isLoading() && searchResults().length === 0 && currentSearchTerm().length >= 2
                ) {
                  <ep-option value="" [disabled]="true" label="No results">
                    <span slot="text" class="text-surface-on-variant"
                      >No clients found matching your search</span
                    >
                  </ep-option>
                }
              </ep-combobox>
            </form>

            <!-- Selected Client Display -->
            @if (selectedClient()) {
              <div class="mb-4 p-3 bg-alt">
                <div class="flex items-center justify-between">
                  <div>
                    <h4 class="font-medium text-primary-on-container">
                      {{ selectedClient()?.name }}
                    </h4>
                    <p class="body-sm text-primary-on-container opacity-80">
                      {{ selectedClient()?.clientId }}
                    </p>
                    @if (selectedClient()?.description) {
                      <p class="body-sm text-primary-on-container opacity-70">
                        {{ selectedClient()?.description }}
                      </p>
                    }
                  </div>
                  <ep-button variant="secondary" size="small" (click)="clearSelection()">
                    Change
                  </ep-button>
                </div>
              </div>
            }
          </div>
        }

        <!-- Navigation buttons -->
        <div class="flex justify-between mt-6">
          <ep-button [routerLink]="['/clients']" variant="secondary"> Back to Clients </ep-button>
          <ep-button variant="primary" [disabled]="!canContinue()" (click)="continue()">
            Continue to User Selection
          </ep-button>
        </div>
      </div>
    }

    <!-- Step 2: User Assignment -->
    @if (currentStep() === 'users') {
      <div class="bg-surface-container rounded-lg p-6">
        <h2 class="heading-md mb-4">Step 2: Assign Users</h2>
        <p class="text-surface-on-variant mb-6">
          Search and assign users as developers or user admins for this client.
        </p>

        <!-- User Search -->
        <div class="mb-6">
          <form [formGroup]="userSearchForm" class="mb-4">
            <ep-textfield
              label="Search Users"
              formControlName="searchTerm"
              variant="search"
              placeholder="Search by username, email, first or last name (min 2 characters)..."
              ngDefaultControl
              (input)="searchUsers()"
            >
              @if (isLoadingUsers()) {
                <div slot="suffix" class="flex items-center gap-2">
                  <div
                    class="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full"
                  ></div>
                  <span class="body-sm" id="user-search-status">Searching...</span>
                </div>
              }
            </ep-textfield>
          </form>

          <!-- User Search Results -->
          @if (userSearchResults().length > 0) {
            <div class="border border-outline rounded-lg">
              <div class="p-3 bg-surface-variant border-b border-outline">
                <h4 class="font-medium">Search Results</h4>
              </div>
              <div class="max-h-64 overflow-y-auto">
                @for (user of userSearchResults(); track user.id) {
                  <div
                    class="p-3 border-b border-outline last:border-b-0 flex items-center justify-between"
                  >
                    <div>
                      <div class="font-medium">{{ user.firstName }} {{ user.lastName }}</div>
                      <div class="body-sm text-surface-on-variant">{{ user.username }}</div>
                      <div class="body-sm text-surface-on-variant">{{ user.email }}</div>
                    </div>
                    <div class="flex gap-2">
                      @if (getUserRole(user) === 'developer') {
                        <ep-button variant="primary" size="small" [disabled]="true">
                          Developer ✓
                        </ep-button>
                        <ep-button variant="secondary" size="small" (click)="addUserAdmin(user)">
                          Make User Admin
                        </ep-button>
                      } @else if (getUserRole(user) === 'user-admin') {
                        <ep-button variant="secondary" size="small" (click)="addDeveloper(user)">
                          Make Developer
                        </ep-button>
                        <ep-button variant="primary" size="small" [disabled]="true">
                          User Admin ✓
                        </ep-button>
                      } @else {
                        <ep-button variant="secondary" size="small" (click)="addDeveloper(user)">
                          Add as Developer
                        </ep-button>
                        <ep-button variant="secondary" size="small" (click)="addUserAdmin(user)">
                          Add as User Admin
                        </ep-button>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          }

          @if (
            userSearchForm.get('searchTerm')?.value &&
            userSearchResults().length === 0 &&
            !isLoadingUsers()
          ) {
            <div class="text-center py-4 text-surface-on-variant border border-outline rounded-lg">
              No users found matching your search.
            </div>
          }
        </div>

        <!-- Selected Users Display -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Developers -->
          <div class="border border-outline rounded-lg">
            <div class="p-4 bg-surface-variant border-b border-outline">
              <h4 class="font-medium">Developers ({{ selectedDevelopers().length }})</h4>
              <p class="body-sm text-surface-on-variant">
                Users who can manage client roles and settings
              </p>
            </div>
            <div class="p-4">
              @if (selectedDevelopers().length === 0) {
                <p class="text-surface-on-variant text-center py-4">No developers assigned</p>
              } @else {
                <div class="space-y-3">
                  @for (user of selectedDevelopers(); track user.id) {
                    <div class="flex items-center justify-between p-2 bg-surface rounded-md">
                      <div>
                        <div class="font-medium body-sm">
                          {{ user.firstName }} {{ user.lastName }}
                        </div>
                        <div class="body-xs text-surface-on-variant">{{ user.email }}</div>
                      </div>
                      <ep-button variant="secondary" size="small" (click)="removeDeveloper(user)">
                        Remove
                      </ep-button>
                    </div>
                  }
                </div>
              }
            </div>
          </div>

          <!-- User Admins -->
          <div class="border border-outline rounded-lg">
            <div class="p-4 bg-surface-variant border-b border-outline">
              <h4 class="font-medium">User Admins ({{ selectedUserAdmins().length }})</h4>
              <p class="body-sm text-surface-on-variant">
                Users who can manage client users and view roles
              </p>
            </div>
            <div class="p-4">
              @if (selectedUserAdmins().length === 0) {
                <p class="text-surface-on-variant text-center py-4">No user admins assigned</p>
              } @else {
                <div class="space-y-3">
                  @for (user of selectedUserAdmins(); track user.id) {
                    <div class="flex items-center justify-between p-2 bg-surface rounded-md">
                      <div>
                        <div class="font-medium body-sm">
                          {{ user.firstName }} {{ user.lastName }}
                        </div>
                        <div class="body-xs text-surface-on-variant">{{ user.email }}</div>
                      </div>
                      <ep-button variant="secondary" size="small" (click)="removeUserAdmin(user)">
                        Remove
                      </ep-button>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Navigation buttons -->
        <div class="flex justify-between mt-6">
          <ep-button variant="secondary" (click)="goBack()"> Back to Client Setup </ep-button>
          <ep-button variant="primary" (click)="continue()"> Continue to Summary </ep-button>
        </div>
      </div>
    }

    <!-- Step 3: Summary -->
    @if (currentStep() === 'summary') {
      <div class="bg-surface-container rounded-lg p-6">
        <h2 class="heading-md mb-4">Step 3: Review & Submit</h2>
        <p class="text-surface-on-variant mb-6">
          Review your selections below and submit to create the client with assigned permissions.
        </p>

        <!-- Client Summary -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="heading-sm">Client Configuration</h3>
            <ep-button variant="secondary" size="small" (click)="editClient()"> Edit </ep-button>
          </div>

          <div class="border border-outline rounded-lg p-4 bg-surface">
            @if (getClientSummary().mode === 'create') {
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="font-medium">Action:</span>
                  <span class="text-surface-on-variant">Create new client</span>
                </div>
                <div class="flex justify-between">
                  <span class="font-medium">Client Name:</span>
                  <span class="text-surface-on-variant">{{ getClientSummary().name }}</span>
                </div>
                @if (getClientSummary().description) {
                  <div class="flex justify-between">
                    <span class="font-medium">Description:</span>
                    <span class="text-surface-on-variant">{{
                      getClientSummary().description
                    }}</span>
                  </div>
                }
                <div class="flex justify-between">
                  <span class="font-medium">Protocol:</span>
                  <span class="text-surface-on-variant">{{
                    getClientSummary().protocol === 'openid-connect' ? 'OpenID Connect' : 'SAML'
                  }}</span>
                </div>
              </div>
            } @else {
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="font-medium">Action:</span>
                  <span class="text-surface-on-variant">Add permissions to existing client</span>
                </div>
                <div class="flex justify-between">
                  <span class="font-medium">Client Name:</span>
                  <span class="text-surface-on-variant">{{ getClientSummary().client?.name }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="font-medium">Client ID:</span>
                  <span class="text-surface-on-variant">{{
                    getClientSummary().client?.clientId
                  }}</span>
                </div>
                @if (getClientSummary().client?.description) {
                  <div class="flex justify-between">
                    <span class="font-medium">Description:</span>
                    <span class="text-surface-on-variant">{{
                      getClientSummary().client?.description
                    }}</span>
                  </div>
                }
              </div>
            }
          </div>
        </div>

        <!-- User Assignments Summary -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="heading-sm">User Assignments</h3>
            <ep-button variant="secondary" size="small" (click)="editUsers()"> Edit </ep-button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Developers Summary -->
            <div class="border border-outline rounded-lg">
              <div class="p-3 bg-surface-variant border-b border-outline">
                <h4 class="font-medium">Developers ({{ selectedDevelopers().length }})</h4>
                <p class="body-sm text-surface-on-variant">Full client management permissions</p>
              </div>
              <div class="p-3">
                @if (selectedDevelopers().length === 0) {
                  <p class="text-surface-on-variant text-center py-2 body-sm">
                    No developers assigned
                  </p>
                } @else {
                  <div class="space-y-2">
                    @for (user of selectedDevelopers(); track user.id) {
                      <div class="flex items-center gap-3 p-2 bg-surface rounded">
                        <div
                          class="w-8 h-8 rounded-full bg-primary-container text-primary flex items-center justify-center body-sm font-medium"
                        >
                          {{ (user.firstName?.charAt(0) || '') + (user.lastName?.charAt(0) || '') }}
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="font-medium body-sm truncate">
                            {{ user.firstName }} {{ user.lastName }}
                          </div>
                          <div class="body-xs text-surface-on-variant truncate">
                            {{ user.email }}
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>

            <!-- User Admins Summary -->
            <div class="border border-outline rounded-lg">
              <div class="p-3 bg-surface-variant border-b border-outline">
                <h4 class="font-medium">User Admins ({{ selectedUserAdmins().length }})</h4>
                <p class="body-sm text-surface-on-variant">User management permissions</p>
              </div>
              <div class="p-3">
                @if (selectedUserAdmins().length === 0) {
                  <p class="text-surface-on-variant text-center py-2 body-sm">
                    No user admins assigned
                  </p>
                } @else {
                  <div class="space-y-2">
                    @for (user of selectedUserAdmins(); track user.id) {
                      <div class="flex items-center gap-3 p-2 bg-surface rounded">
                        <div
                          class="w-8 h-8 rounded-full bg-secondary-container text-secondary flex items-center justify-center body-sm font-medium"
                        >
                          {{ (user.firstName?.charAt(0) || '') + (user.lastName?.charAt(0) || '') }}
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="font-medium body-sm truncate">
                            {{ user.firstName }} {{ user.lastName }}
                          </div>
                          <div class="body-xs text-surface-on-variant truncate">
                            {{ user.email }}
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Validation Messages -->
        @if (hasErrors() && !submissionSuccess()) {
          <div class="mb-6 p-4 bg-error-container border border-error rounded-lg">
            <div class="flex items-center gap-2 mb-2">
              <svg class="w-5 h-5 text-error" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span class="font-medium text-error-on-container">
                @if (validationErrors().length > 0) {
                  Validation Error
                } @else {
                  Submission Failed
                }
              </span>
            </div>
            <div class="space-y-1">
              @for (error of getAllErrors(); track error) {
                <p class="body-sm text-error-on-container">• {{ error }}</p>
              }
            </div>
            @if (networkError() && retryCount() < maxRetries) {
              <div class="mt-3">
                <ep-button variant="secondary" size="small" (click)="retrySubmission()">
                  Retry ({{ retryCount() + 1 }}/{{ maxRetries }})
                </ep-button>
              </div>
            }
          </div>
        }

        <!-- Submission Progress -->
        @if (isSubmitting()) {
          <div class="mb-6 p-4 bg-primary-container border border-primary rounded-lg">
            <div class="flex items-center gap-2 mb-3">
              <div
                class="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full"
              ></div>
              <span class="font-medium text-primary-on-container">{{
                getSubmissionStepDescription()
              }}</span>
            </div>
            <div class="w-full bg-primary-container-variant rounded-full h-2">
              <div
                class="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                [style.width.%]="getProgressPercentage()"
              ></div>
            </div>
            <p class="body-sm text-primary-on-container mt-2 opacity-80">
              {{ getProgressPercentage() }}% complete
            </p>
          </div>
        }

        <!-- Success Message -->
        @if (submissionSuccess()) {
          <div class="mb-6 p-4 bg-success-container border border-success rounded-lg">
            <div class="flex items-center gap-2">
              <svg class="w-5 h-5 text-success" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span class="font-medium text-success-on-container">Success!</span>
            </div>
            <p class="body-sm text-success-on-container mt-2">
              Client and permissions have been created successfully. All admin permissions are now
              configured.
            </p>
          </div>
        }

        <!-- Navigation buttons -->
        <div class="flex justify-between">
          <ep-button
            variant="secondary"
            (click)="goBack()"
            [disabled]="isSubmitting() || isValidating()"
            aria-label="Go back to user assignment step"
          >
            Back to User Assignment
          </ep-button>
          @if (!submissionSuccess()) {
            <ep-button
              variant="primary"
              [disabled]="!canSubmit() || isSubmitting() || isValidating()"
              (click)="submit()"
            >
              @if (isSubmitting()) {
                <span class="flex items-center gap-2">
                  <div
                    class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                  ></div>
                  {{ getSubmissionStepDescription() }}
                </span>
              } @else if (isValidating()) {
                <span class="flex items-center gap-2">
                  <div
                    class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                  ></div>
                  Validating...
                </span>
              } @else {
                Create Client & Permissions
              }
            </ep-button>
          } @else {
            <ep-button
              variant="secondary"
              [routerLink]="['/clients']"
              aria-label="Return to clients list"
            >
              Back to Clients
            </ep-button>
          }
        </div>
      </div>
    }
  </section>
</div>
